import { useMemo, useState } from 'react';

import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { getReservedBylineIconCount } from '../ssr/outboundBylineSsrState.ts';
import { useOutboundBylineSsrState } from '../ssr/useOutboundBylineSsrState.ts';
import { useIncomingLinksSsrState } from '../ssr/useIncomingLinksSsrState.ts';
import { useRelatedContentSsrState } from '../ssr/useRelatedContentSsrState.ts';
import { shouldPaintBylineSsrPlaceholder } from '../ssr/shouldPaintBylineSsrPlaceholder.ts';
import {
	getBylineConnectionIcons,
	MAX_BYLINE_CONNECTION_ICONS,
} from '../utils/getBylineConnectionIcons.ts';
import { getBylineVisibility, type BylineVisibility } from '../utils/getBylineVisibility.ts';
import { getDevDocsConnectionIcons } from '../utils/getDevDocsConnectionIcons.ts';
import type { BylineContentType } from '../utils/contentAri.ts';
import type { useBylineConnectionsData } from './useBylineConnectionsData.ts';

type BylineConnectionsData = ReturnType<typeof useBylineConnectionsData>;

type BylineConnectionsVisibilityOptions = {
	contentId: string;
	contentType: BylineContentType;
	isEmbeddedMenu: boolean;
	data: {
		outgoing: Pick<BylineConnectionsData['outgoing'], 'links' | 'loading'>;
		incoming: Pick<BylineConnectionsData['incoming'], 'twgLinks' | 'loading'>;
		relatedContent: Pick<BylineConnectionsData['relatedContent'], 'links' | 'loading'>;
		devDocsConnection: BylineConnectionsData['devDocsConnection'];
	};
};

type BylineConnectionsVisibility = BylineVisibility & {
	hiddenBySsr: boolean;
	devDocsIcons: ReturnType<typeof getDevDocsConnectionIcons>;
};

/** Combines loaded visibility rules with the server's retained hide decision. */
export const useBylineConnectionsVisibility = ({
	contentId,
	contentType,
	isEmbeddedMenu,
	data,
}: BylineConnectionsVisibilityOptions): BylineConnectionsVisibility => {
	const { outgoing, incoming, relatedContent, devDocsConnection } = data;
	const { links: outgoingLinks, loading: outgoingLinksLoading } = outgoing;
	const { twgLinks: twgIncomingLinks, loading: incomingLinksLoading } = incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = relatedContent;
	const devDocsIcons = useMemo(
		() => getDevDocsConnectionIcons(devDocsConnection),
		[devDocsConnection],
	);
	// Outgoing links fill the stack first, so once they alone fill it no incoming icon can change
	// what is painted and the trigger need not wait for the graph query.
	const outgoingFillsStack = useMemo(
		() =>
			getBylineConnectionIcons(outgoingLinks, devDocsIcons).length >= MAX_BYLINE_CONNECTION_ICONS,
		[outgoingLinks, devDocsIcons],
	);
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingState = useIncomingLinksSsrState(contentId, contentType);
	const relatedContentState = useRelatedContentSsrState(contentId, contentType);
	const isSSROrHydrating = useIsSSROrHydrating();
	// Keep the server's hide decision until navigation, even if client queries find more links.
	const [hiddenSsrContent, setHiddenSsrContent] = useState(() =>
		isSSROrHydrating &&
		!shouldPaintBylineSsrPlaceholder({
			outboundState,
			incomingLinksState: incomingState,
			relatedContentState,
			hasDevDocsConnection: devDocsConnection !== null,
		})
			? { contentId, contentType }
			: null,
	);
	const hiddenBySsr =
		hiddenSsrContent?.contentId === contentId && hiddenSsrContent.contentType === contentType;
	if (hiddenSsrContent && !hiddenBySsr) {
		setHiddenSsrContent(null);
	}

	const reservedIconCount = getReservedBylineIconCount(
		outboundState,
		devDocsConnection !== null,
		incomingState?.status === 'present',
	);
	// The show/hide and wait rules for both menus live in `getBylineVisibility`.
	const { hasConnection, isResolving, shouldRenderByline } = useMemo(
		() =>
			getBylineVisibility({
				isEmbeddedMenu,
				outgoingLinks,
				incomingLinks: twgIncomingLinks,
				relatedContentLinks,
				hasDevDocsConnection: devDocsConnection !== null,
				outgoingLinksLoading,
				incomingLinksLoading,
				relatedContentLoading,
				outgoingFillsStack,
				reservedIconCount,
			}),
		[
			isEmbeddedMenu,
			outgoingLinks,
			twgIncomingLinks,
			relatedContentLinks,
			devDocsConnection,
			outgoingLinksLoading,
			incomingLinksLoading,
			relatedContentLoading,
			outgoingFillsStack,
			reservedIconCount,
		],
	);

	return { hiddenBySsr, hasConnection, isResolving, shouldRenderByline, devDocsIcons };
};
